import React, { useState } from 'react'
import { Link } from 'gatsby'
import Markdown from 'markdown-to-jsx'
import BgImage from '../../components/bgImage'
import Institutions from '../../pages/jobtype/institutions'
import { Container, Grid, Typography, Button, Avatar, useTheme, Box } from '@mui/material'
import SentimentVeryDissatisfiedIcon from '@mui/icons-material/SentimentVeryDissatisfied'
import SentimentSatisfiedIcon from '@mui/icons-material/SentimentSatisfied'
import SentimentVerySatisfiedIcon from '@mui/icons-material/SentimentVerySatisfied'
import TrendingDownIcon from '@mui/icons-material/TrendingDown'
import TrendingFlatIcon from '@mui/icons-material/TrendingFlat'
import TrendingUpIcon from '@mui/icons-material/TrendingUp'
import ThumbUpIcon from '@mui/icons-material/ThumbUp'
import ThumbDownIcon from '@mui/icons-material/ThumbDown'
import { TRAINING_ID, TENDER_ID } from '../../resources/constants'

const JobTypeMain = ({ data }) => {
	const theme = useTheme()
	const [open, setOpen] = useState(false)
	const [institutions, setInstitutions] = useState(null)
	const [program, setProgram] = useState(null)

	const handleClickOpen = (program) => {
		setProgram(program)
		setInstitutions(
			data.allStrapiInstitution.edges.filter(({ node }) => node.Programs.some(({ id }) => id === program.id))
		)
		setOpen(true)
	}

	const handleClose = () => {
		setOpen(false)
	}

	return (
		<main>
			<Institutions open={open} onClose={handleClose} institutions={institutions} program={program} />
			<BgImage
				title={data.strapiCareer.Title}
				fluid={data.strapiCareer.Image?.file?.childImageSharp?.gatsbyImageData}
				overlayColor='rgba(80,80,80,0.5)'
				height={250}
			>
				<Container maxWidth='md' sx={{ pt: '30px', pb: '50px' }}>
					<Box sx={{ mt: '50px', color: '#fff', textAlign: 'center' }}>
						<Typography variant='h4'>{data.strapiCareer.Title}</Typography>
						<Typography variant='h3' sx={{ lineHeight: '90px', fontWeight: 500 }}>
							{data.strapiJobtype.Title}
						</Typography>
					</Box>
				</Container>
			</BgImage>
			<Container maxWidth='md' sx={{ pt: '30px', pb: '50px', backgroundColor: '#fff' }}>
				<Grid container spacing={6}>
					<Grid item xs={12} key='1'>
						<Typography variant='h5' color='textPrimary' sx={{ mb: 2.5 }}>
							Description:
						</Typography>
						<Typography variant='subtitle1' color='textPrimary'>
							{data.strapiJobtype.Description}
						</Typography>
					</Grid>

					<Grid item xs={12} key='2'>
						<Typography variant='h5' color='textPrimary' sx={{ mb: 2.5 }}>
							Key tasks:
						</Typography>
						<Typography
							variant='subtitle1'
							color='textPrimary'
							style={{
								'& li': {
									listStylePosition: 'inside',
									listStyle: 'inherit'
								}
							}}
						>
							{data.strapiJobtype.Keytasks ? <Markdown>{data.strapiJobtype.Keytasks}</Markdown> : '-'}
						</Typography>
					</Grid>

					<Grid item xs={12} key='3'>
						<Typography variant='h5' color='textPrimary' sx={{ mb: 2.5 }}>
							Programs:
						</Typography>
						<Typography variant='subtitle1' color='textPrimary'>
							{`These programs lead to the job of ${data.strapiJobtype.Title}:`}
						</Typography>
						{data.strapiJobtype.Programs?.map((program) => (
							<Box key={program.id} sx={{ display: 'flex', alignItems: 'center', m: '12px 0' }}>
								<Button
									color='secondary'
									variant='outlined'
									onClick={() => handleClickOpen(program)}
									sx={{ mr: '10px', boxShadow: 'none', minWidth: 0, textTransform: 'none' }}
								>
									{program.Title}
								</Button>
							</Box>
						))}
					</Grid>

					<Grid item xs={12} md={4} key='4'>
						<Typography variant='h5' color='textPrimary' sx={{ mb: 2.5 }}>
							Demand:
						</Typography>
						{data.strapiJobtype.Demand != null ? (
							<Box
								sx={{
									backgroundColor: '#eee',
									borderRadius: '5px',
									color: theme.palette.text.accent,
									display: 'inline-flex',
									p: '10px'
								}}
							>
								{data.strapiJobtype.Demand.Icon === 'trending_up' ? (
									<TrendingUpIcon sx={{ mr: '10px' }} />
								) : data.strapiJobtype.Demand.Icon === 'trending_flat' ? (
									<TrendingFlatIcon sx={{ mr: '10px' }} />
								) : data.strapiJobtype.Demand.Icon === 'trending_down' ? (
									<TrendingDownIcon sx={{ mr: '10px' }} />
								) : (
									'-'
								)}
								<Typography variant='subtitle1'>{data.strapiJobtype.Demand.Title}</Typography>
							</Box>
						) : (
							'unknown'
						)}
					</Grid>

					<Grid item xs={12} md={4} key='5'>
						<Typography variant='h5' color='textPrimary' sx={{ mb: 2.5 }}>
							Salary level:
						</Typography>
						{data.strapiJobtype.Salarylevel != null ? (
							<Box
								sx={{
									backgroundColor: '#eee',
									borderRadius: '5px',
									color: theme.palette.text.accent,
									display: 'inline-flex',
									p: '10px'
								}}
							>
								{data.strapiJobtype.Salarylevel.Icon === 'sentiment_very_satisfied' ? (
									<SentimentVerySatisfiedIcon sx={{ mr: '10px' }} />
								) : data.strapiJobtype.Salarylevel.Icon === 'sentiment_satisfied' ? (
									<SentimentSatisfiedIcon sx={{ mr: '10px' }} />
								) : data.strapiJobtype.Salarylevel.Icon === 'sentiment_very_dissatisfied' ? (
									<SentimentVeryDissatisfiedIcon sx={{ mr: '10px' }} />
								) : (
									'-'
								)}
								<Typography variant='subtitle1'>{data.strapiJobtype.Salarylevel.Title}</Typography>
							</Box>
						) : (
							'unknown'
						)}
					</Grid>

					<Grid item xs={12} md={4} key='6'>
						<Typography variant='h5' color='textPrimary' sx={{ mb: 2.5 }}>
							Scholarship opportunity:
						</Typography>
						{data.strapiJobtype.Scholarshippriority != null ? (
							<Box
								sx={{
									backgroundColor: '#eee',
									borderRadius: '5px',
									color: theme.palette.text.accent,
									display: 'inline-flex',
									p: '10px'
								}}
							>
								{data.strapiJobtype.Scholarshippriority.Icon === 'thumb_up' ? (
									<ThumbUpIcon sx={{ mr: '10px' }} />
								) : data.strapiJobtype.Scholarshippriority.Icon === 'thumb_down' ? (
									<ThumbDownIcon sx={{ mr: '10px' }} />
								) : (
									'-'
								)}
								<Typography variant='subtitle1'>{data.strapiJobtype.Scholarshippriority.Title}</Typography>
							</Box>
						) : (
							'unknown'
						)}
					</Grid>

					<Grid item xs={12} key='7'>
						<Typography variant='h5' color='textPrimary' sx={{ mb: 2.5 }}>
							Related Job Types:
						</Typography>
						<Typography variant='subtitle1' color='textPrimary'>
							{data.strapiJobtype.Specialties ? <Markdown>{data.strapiJobtype.Specialties}</Markdown> : '-'}
						</Typography>
					</Grid>

					<Grid item xs={12} key='8'>
						<Typography variant='h5' color='textPrimary' sx={{ mb: 2.5 }}>
							Testimonial:
						</Typography>
						{data.strapiJobtype.Testimonymedia ? (
							<Avatar
								alt={data.strapiJobtype.Testimonygiver}
								src={data.strapiJobtype.Testimonymedia?.file?.childImageSharp?.fluid}
								sx={{ float: 'right', ml: '30px', width: 120, height: 120 }}
							/>
						) : null}
						<Typography variant='subtitle1' color='textPrimary'>
							{data.strapiJobtype.Testimonytext ? <Markdown>{data.strapiJobtype.Testimonytext}</Markdown> : '-'}
						</Typography>
						<Typography variant='subtitle2' color='textPrimary' sx={{ mt: '20px', mb: '40px', fontStyle: 'italic' }}>
							{data.strapiJobtype.Testimonygiver}
						</Typography>
						{data.strapiJobtype.Testimonyvideo ? (
							<Box
								sx={{
									marginBottom: 4,
									position: 'relative',
									pt: '56.25%',
									height: 0,
									overflow: 'hidden',
									'& iframe, & object, & embed': {
										position: 'absolute',
										top: 0,
										left: 0,
										width: '100%',
										height: '100%'
									}
								}}
								dangerouslySetInnerHTML={{ __html: data.strapiJobtype.Testimonyvideo }}
							/>
						) : null}
					</Grid>

					<Grid item xs={12} key='9'>
						<Typography variant='h5' color='textPrimary' sx={{ mb: 2.5 }}>
							Current vacancies:
						</Typography>
						<Typography variant='subtitle1' color='textPrimary'>
							{data.allStrapiJob.edges.map(({ node }) => {
								const { strapiId, Title, Adverttype } = node
								return (
									<Box key={strapiId}>
										<Link
											to={`/${
												Adverttype?.id === TRAINING_ID ? 'training' : Adverttype?.id === TENDER_ID ? 'tender' : 'job'
											}/detail/${strapiId}`}
											style={{ marginLeft: -8, textDecoration: 'none' }}
										>
											<Button color='secondary' sx={{ textTransform: 'none' }}>
												{Title}
											</Button>
										</Link>
									</Box>
								)
							})}
						</Typography>
					</Grid>

					<Grid item xs={12} key='10'>
						<Typography variant='h5' color='textPrimary' sx={{ mb: 2.5 }}>
							Service providers:
						</Typography>
						<Typography variant='subtitle1' color='textPrimary'>
							These people are professionals in this job:
						</Typography>
						<Typography variant='subtitle1' color='textPrimary'>
							{data.allStrapiServiceprovider?.edges?.map(({ node }) => {
								const { strapiId, Name } = node
								return (
									<Box key={strapiId}>
										<Link to={`/serviceprovider/detail/${strapiId}`} style={{ marginLeft: -8, textDecoration: 'none' }}>
											<Button color='secondary' sx={{ textTransform: 'none' }}>
												{Name}
											</Button>
										</Link>
									</Box>
								)
							})}
						</Typography>
					</Grid>
				</Grid>
			</Container>
		</main>
	)
}

export default JobTypeMain
